import styles from './FaqModule.scss';

import cx from 'classnames';
import { ReferencedEntry } from 'contentful';
import React, { FC, HTMLAttributes } from 'react';

import { Faq, FaqModule as FaqModuleType } from 'src/api/contentful/contentTypes.cloverdotcom';
import { FaqButtonAccordion } from 'src/components/Accordion/FaqButtonAccordion/FaqButtonAccordion';
import { AccordionItem } from 'src/components/Accordion/models';
import { FadeToastUp } from 'src/components/Animation/FadeToastUp/FadeToastUp';
import { Grid } from 'src/components/Grid/Grid';
import { GridContainer } from 'src/components/GridContainer/GridContainer';
import { TextV2 } from 'src/components/TextV2/TextV2';
import { FlatEntry, flattenContentfulData } from 'src/services/cms/flattenContentful';
import { EntriesV2 } from 'src/services/cms/typesUtil';

interface FaqModuleProps extends HTMLAttributes<HTMLElement> {
  entry?: FlatEntry<FaqModuleType>;
}

const FaqModule: FC<FaqModuleProps> = ({
  entry,
  className,
  style = {},
  ...props
}) => {
  if (!entry) return null;
  const { backgroundColor, items = [], title, titleColor } = entry;

  const TitleContainer = () => (
    <GridContainer justifyContent="center">
      <Grid item xs={12} lg={10} xl={12} md>
        <FadeToastUp transitionDuration={1500} threshold={.1}>
          <div>
            <TextV2 isMarkdown value={title} entry={{ ...entry, field: 'title' }} tagName="h2" variant="h3" className={styles.faqModuleTitle} style={{ color: titleColor }} />
          </div>
        </FadeToastUp>
      </Grid>
    </GridContainer>
  );

  const getAccordionItems = (accordionItems: EntriesV2<FaqModuleType> = []): AccordionItem[] => {
    return accordionItems
    .filter(item => !!item)
    .map((item: FlatEntry<Faq>, index) => ({
      id: index,
      title: <TextV2 tagName="h3" variant="h5" entry={{ ...item, field: 'question' }} value={item.question} className={cx(styles.faqModuleQuestion)} />,
      body: <TextV2 tagName="div" variant="body" entry={{ ...item, field: 'answer' }} isMarkdown value={item.answer} className={cx(styles.faqModuleAnswer)} />,
    }));
  };

  return (
    <section className={cx(styles.faqModule, className)} style={{ backgroundColor: backgroundColor || style.backgroundColor }} {...props}>
      {title && <TitleContainer />}
      <GridContainer justifyContent="center">
        <Grid item xs={12} lg={10} xl={12} md>
          <FadeToastUp >
            <div className={cx(styles.faqModuleCells)}>
              <FaqButtonAccordion items={getAccordionItems(items)} />
            </div>
          </FadeToastUp>
        </Grid>
      </GridContainer>
    </section>
  );
};

FaqModule.displayName = 'FaqModule';

interface FaqModuleWrapperProps extends HTMLAttributes<HTMLDivElement> {
  entry?: ReferencedEntry<FaqModuleType>;
}

const FaqModuleWrapper: FC<FaqModuleWrapperProps> = ({ entry, ...props }) =>
  <FaqModule {...props} entry={flattenContentfulData(entry)} />;

FaqModuleWrapper.displayName = 'FaqModuleWrapper';

export {
  FaqModule as _FaqModule,
  FaqModuleWrapper as FaqModule,
};
